import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const certificationsData = [
  {
    id: 1,
    title: "AWS Certified Solutions Architect",
    issuer: "Amazon Web Services",
    date: "2024",
    credentialId: "AWS-SA-2024-001",
    description: "Professional certification for designing distributed systems on AWS",
    skills: ["Cloud Architecture", "AWS Services", "Security", "Scalability"],
    color: "from-orange-500/20 to-yellow-500/20",
    borderColor: "border-orange-500/40",
    glowColor: "shadow-orange-500/20",
    verified: true
  },
  {
    id: 2,
    title: "Google Cloud Professional Developer",
    issuer: "Google Cloud",
    date: "2023",
    credentialId: "GCP-DEV-2023-042",
    description: "Expertise in developing scalable and reliable applications on Google Cloud Platform",
    skills: ["GCP", "Kubernetes", "CI/CD", "Microservices"],
    color: "from-blue-500/20 to-cyan-500/20",
    borderColor: "border-blue-500/40",
    glowColor: "shadow-blue-500/20",
    verified: true
  },
  {
    id: 3,
    title: "Meta Front-End Developer",
    issuer: "Meta (Facebook)",
    date: "2023",
    credentialId: "META-FE-2023-156",
    description: "Professional certificate in modern front-end development with React",
    skills: ["React", "JavaScript", "HTML/CSS", "UI/UX"],
    color: "from-purple-500/20 to-pink-500/20",
    borderColor: "border-purple-500/40",
    glowColor: "shadow-purple-500/20",
    verified: true
  },
  {
    id: 4,
    title: "Docker Certified Associate",
    issuer: "Docker Inc.",
    date: "2023",
    credentialId: "DCA-2023-789",
    description: "Certification in containerization and orchestration with Docker",
    skills: ["Docker", "Containerization", "DevOps", "Orchestration"],
    color: "from-cyan-500/20 to-blue-500/20",
    borderColor: "border-cyan-500/40",
    glowColor: "shadow-cyan-500/20",
    verified: true
  },
  {
    id: 5,
    title: "MongoDB Certified Developer",
    issuer: "MongoDB University",
    date: "2023",
    credentialId: "MDB-DEV-2023-321",
    description: "Professional certification for MongoDB database development and optimization",
    skills: ["MongoDB", "NoSQL", "Database Design", "Performance"],
    color: "from-green-500/20 to-emerald-500/20",
    borderColor: "border-green-500/40",
    glowColor: "shadow-green-500/20",
    verified: true
  },
  {
    id: 6,
    title: "Certified Kubernetes Administrator",
    issuer: "Cloud Native Computing Foundation",
    date: "2024",
    credentialId: "CKA-2024-567",
    description: "Expert-level certification in Kubernetes cluster administration",
    skills: ["Kubernetes", "Container Orchestration", "Cloud Native", "DevOps"],
    color: "from-indigo-500/20 to-purple-500/20",
    borderColor: "border-indigo-500/40",
    glowColor: "shadow-indigo-500/20",
    verified: true
  }
];

const VerifiedBadge = () => (
  <motion.div
    className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500/10 border border-green-500/30 rounded-full text-xs text-green-400 font-medium"
    initial={{ scale: 0, rotate: -180 }}
    animate={{ scale: 1, rotate: 0 }}
    transition={{ 
      type: "spring", 
      stiffness: 260, 
      damping: 20,
      delay: 0.2
    }}
  >
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="14" 
      height="14" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    >
      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
      <path d="m9 12 2 2 4-4"/>
    </svg>
    Verified
  </motion.div>
);

const CertificationCard = ({ cert, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ 
        duration: 0.6, 
        delay: index * 0.1,
        ease: [0.22, 1, 0.36, 1]
      }}
      className="relative group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card Container */}
      <motion.div
        className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${cert.color} backdrop-blur-sm border ${cert.borderColor} transition-all duration-500`}
        whileHover={{ 
          scale: 1.02,
          boxShadow: `0 20px 40px -12px ${cert.glowColor}`
        }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Animated Background Gradient */}
        <motion.div
          className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-500"
          style={{
            background: `radial-gradient(circle at ${isHovered ? '50%' : '0%'} ${isHovered ? '50%' : '0%'}, rgba(255,255,255,0.1) 0%, transparent 70%)`
          }}
        />

        {/* Shimmer Effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent"
          initial={{ x: '-100%' }}
          animate={isHovered ? { x: '100%' } : { x: '-100%' }}
          transition={{ duration: 1.5, ease: "easeInOut" }}
        />

        <div className="relative p-6">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4">
              {/* Award Icon */}
              <motion.div
                className={`p-3 rounded-xl bg-white/5 border ${cert.borderColor}`}
                whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 0.5 }}
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="24" 
                  height="24" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className="text-white"
                >
                  <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"/>
                  <circle cx="12" cy="8" r="6"/>
                </svg>
              </motion.div>

              {/* Title & Issuer */}
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-1 group-hover:text-white/90 transition-colors">
                  {cert.title}
                </h3>
                <p className="text-gray-400 text-sm font-medium">{cert.issuer}</p>
              </div>
            </div>

            {/* Verified Badge */}
            {cert.verified && <VerifiedBadge />}
          </div>

          {/* Date & Credential ID */}
          <div className="flex items-center gap-4 mb-4 text-sm">
            <div className="flex items-center gap-2 text-gray-400">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                <line x1="16" y1="2" x2="16" y2="6"/>
                <line x1="8" y1="2" x2="8" y2="6"/>
                <line x1="3" y1="10" x2="21" y2="10"/>
              </svg>
              {cert.date}
            </div>
            <div className="flex items-center gap-2 text-gray-500 text-xs">
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="14" 
                height="14" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
              </svg>
              {cert.credentialId}
            </div>
          </div>

          {/* Description */}
          <p className="text-gray-300 text-sm leading-relaxed mb-4">
            {cert.description}
          </p>

          {/* Skills Tags */}
          <div className="flex flex-wrap gap-2 mb-4">
            {cert.skills.map((skill, idx) => (
              <motion.span
                key={skill}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 + idx * 0.05 }}
                className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs font-medium text-gray-300 hover:bg-white/10 hover:border-white/20 transition-all cursor-default"
              >
                {skill}
              </motion.span>
            ))}
          </div>

          {/* Expand Button */}
          <motion.button
            className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors mt-2"
            whileHover={{ x: 5 }}
          >
            <span>{isExpanded ? 'Show less' : 'View details'}</span>
            <motion.svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <polyline points="6 9 12 15 18 9"/>
            </motion.svg>
          </motion.button>

          {/* Expanded Content */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
              >
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="space-y-3">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-300 mb-2">Certification Benefits:</h4>
                      <ul className="space-y-2 text-sm text-gray-400">
                        <li className="flex items-start gap-2">
                          <svg className="w-4 h-4 mt-0.5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                          Industry-recognized professional certification
                        </li>
                        <li className="flex items-start gap-2">
                          <svg className="w-4 h-4 mt-0.5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                          Validated expertise in {cert.skills[0]}
                        </li>
                        <li className="flex items-start gap-2">
                          <svg className="w-4 h-4 mt-0.5 text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                          </svg>
                          Continuous learning and skill development
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Decorative Corner */}
        <div className="absolute top-0 right-0 w-32 h-32 opacity-20">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle cx="70" cy="30" r="40" fill="url(#gradient)" opacity="0.3"/>
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="white" stopOpacity="0.2"/>
                <stop offset="100%" stopColor="white" stopOpacity="0"/>
              </linearGradient>
            </defs>
          </svg>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function Certifications() {
  const [filter, setFilter] = useState('all');
  
  const years = ['all', '2024', '2023'];

  const filteredCerts = filter === 'all' 
    ? certificationsData 
    : certificationsData.filter(cert => cert.date === filter);

  return (
    <div className="relative min-h-screen py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          {/* Icon Badge */}
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ 
              type: "spring", 
              stiffness: 200, 
              damping: 15,
              delay: 0.2
            }}
            className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-2xl bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="32" 
              height="32" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="text-yellow-400"
            >
              <path d="m15.477 12.89 1.515 8.526a.5.5 0 0 1-.81.47l-3.58-2.687a1 1 0 0 0-1.197 0l-3.586 2.686a.5.5 0 0 1-.81-.469l1.514-8.526"/>
              <circle cx="12" cy="8" r="6"/>
            </svg>
          </motion.div>

          <h2 className="text-4xl sm:text-5xl font-bold mb-4 bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
            Certifications & Achievements
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Professional certifications that validate my expertise across various technologies and platforms
          </p>

          {/* Stats */}
          <div className="flex items-center justify-center gap-8 mt-8">
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="text-3xl font-bold text-white mb-1">
                {certificationsData.length}+
              </div>
              <div className="text-sm text-gray-500">Certifications</div>
            </motion.div>
            <div className="w-px h-12 bg-gray-700" />
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-center"
            >
              <div className="text-3xl font-bold text-white mb-1">
                {certificationsData.filter(c => c.verified).length}
              </div>
              <div className="text-sm text-gray-500">Verified</div>
            </motion.div>
            <div className="w-px h-12 bg-gray-700" />
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <div className="text-3xl font-bold text-white mb-1">
                5+
              </div>
              <div className="text-sm text-gray-500">Platforms</div>
            </motion.div>
          </div>
        </motion.div>

        {/* Filter Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex items-center justify-center gap-3 mb-12"
        >
          {years.map((year) => (
            <motion.button
              key={year}
              onClick={() => setFilter(year)}
              className={`px-6 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 ${
                filter === year
                  ? 'bg-white text-black shadow-lg shadow-white/20'
                  : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 hover:border-white/20'
              }`}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              {year === 'all' ? 'All Certifications' : year}
            </motion.button>
          ))}
        </motion.div>

        {/* Certifications Grid */}
        <motion.div
          layout
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          <AnimatePresence mode="popLayout">
            {filteredCerts.map((cert, index) => (
              <CertificationCard key={cert.id} cert={cert} index={index} />
            ))}
          </AnimatePresence>
        </motion.div>

        {/* Empty State */}
        {filteredCerts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="text-6xl mb-4">🏆</div>
            <h3 className="text-xl font-semibold text-gray-400 mb-2">
              No certifications found
            </h3>
            <p className="text-gray-500">
              Try selecting a different year filter
            </p>
          </motion.div>
        )}

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center mt-16"
        >
          <div className="inline-block p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/0 border border-white/10">
            <h3 className="text-2xl font-bold text-white mb-3">
              Continuous Learning Journey
            </h3>
            <p className="text-gray-400 mb-6 max-w-lg">
              I'm always pursuing new certifications to stay current with the latest technologies and best practices
            </p>
            <motion.div
              className="flex items-center justify-center gap-2 text-sm text-gray-500"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd"/>
              </svg>
              More certifications coming soon...
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
